import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  LogOut, TrendingUp, Users, Wallet, CheckCircle2,
  Plus, X, Clock, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "../lib/api";
import { usePartner } from "../context/PartnerContext";
import { BrandLogo } from "../components/BrandLogo";

export default function PartnerDashboard() {
  const { partner, logout } = usePartner();
  const [stats, setStats] = useState(null);
  const [referidos, setReferidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRefer, setShowRefer] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        http.get("/partners/stats"),
        http.get("/partners/referidos"),
      ]);
      setStats(s.data);
      setReferidos(r.data || []);
    } catch (e) {
      // 401 etc
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (partner && partner.id && partner.estado === "aprobado") load();
  }, [partner]);

  if (partner === null) {
    return <div className="min-h-screen flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>;
  }
  if (!partner) return <Navigate to="/partner/login" replace />;

  if (partner.estado === "pendiente") {
    return <PendingApproval partner={partner} onLogout={async () => { await logout(); nav("/partner/login"); }} />;
  }
  if (partner.estado === "rechazado" || partner.estado === "suspendido") {
    return <BlockedAccount partner={partner} onLogout={async () => { await logout(); nav("/partner/login"); }} />;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-soft)" }}>
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="container-x flex items-center justify-between py-4">
          <Link to="/">
            <BrandLogo size={40} subline="Panel partner"/>
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>{partner.nombre} {partner.apellido}</div>
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{partner.email}</div>
            </div>
            <button onClick={async () => { await logout(); nav("/partner/login"); }} className="btn-outline" style={{ padding: "0.55rem 1rem", fontSize: 13 }} data-testid="partner-logout">
              <LogOut size={14}/> Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-10" data-testid="partner-dashboard">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: "var(--brand-accent)" }}>Tu panel</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
            Hola, {partner.nombre} 👋
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Comisión {partner.comision_pct || 12}% sobre prima anual de cada póliza contratada que refieras.
          </p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Wallet} color="var(--brand-accent)" label="Comisión acumulada" value={loading ? "—" : `UF ${stats?.comision_acumulada_uf?.toFixed(2) || "0,00"}`} highlight/>
          <StatCard icon={Clock} color="#F57C00" label="Comisión pendiente" value={loading ? "—" : `UF ${stats?.comision_pendiente_uf?.toFixed(2) || "0,00"}`}/>
          <StatCard icon={Users} color="var(--brand-navy)" label="Referidos totales" value={loading ? "—" : stats?.total_referidos || 0}/>
          <StatCard icon={CheckCircle2} color="#2E7D32" label="Pólizas contratadas" value={loading ? "—" : stats?.referidos_contratados || 0}/>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Referidos table */}
          <div className="lg:col-span-2 bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              <div>
                <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Mis referidos</h3>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>Estado y comisión de cada comunidad referida</p>
              </div>
              <button className="btn-primary" style={{ padding: "0.55rem 1rem", fontSize: 13 }} onClick={() => setShowRefer(true)} data-testid="btn-referir">
                <Plus size={14}/> Referir comunidad
              </button>
            </div>
            {loading ? (
              <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
            ) : referidos.length === 0 ? (
              <div className="p-10 text-center">
                <Users size={36} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }}/>
                <div className="font-bold mb-1" style={{ fontFamily: "Manrope" }}>Aún no tienes referidos</div>
                <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>Refiere tu primera comunidad y empieza a generar comisiones.</div>
                <button className="btn-accent" onClick={() => setShowRefer(true)}>
                  Referir mi primera comunidad <ArrowRight size={14}/>
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="tbl">
                  <thead><tr><th>Comunidad</th><th>Contacto</th><th>Estado</th><th>Comisión UF</th><th>Pago</th><th>Fecha</th></tr></thead>
                  <tbody>
                    {referidos.map((r) => (
                      <tr key={r.id}>
                        <td className="font-semibold">{r.razon_social}<div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.comuna}</div></td>
                        <td>{r.contacto_nombre}<div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.contacto_email}</div></td>
                        <td><span className={`badge badge--${(r.estado === "contratada" ? "vigente" : r.estado === "cotizada" ? "en_revision" : r.estado === "perdida" ? "vencida" : "nueva")}`}>{r.estado}</span></td>
                        <td>{r.comision_uf ? `UF ${r.comision_uf.toFixed(2)}` : "—"}</td>
                        <td><span className={`badge ${r.comision_pagada ? "badge--vigente" : "badge--pendiente_firma"}`}>{r.comision_pagada ? "Pagada" : "Pendiente"}</span></td>
                        <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>{(r.created_at || "").slice(0,10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Side info */}
          <div className="space-y-5">
            <div className="bg-white border rounded-xl p-6" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={18} style={{ color: "var(--brand-accent)" }}/>
                <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Resumen pagos</h3>
              </div>
              <div className="space-y-3 text-sm">
                <Row label="Acumulado" value={`UF ${stats?.comision_acumulada_uf?.toFixed(2) || "0,00"}`} bold/>
                <Row label="Pagado" value={`UF ${stats?.comision_pagada_uf?.toFixed(2) || "0,00"}`}/>
                <Row label="Pendiente" value={`UF ${stats?.comision_pendiente_uf?.toFixed(2) || "0,00"}`} highlight/>
              </div>
              <div className="mt-4 pt-4 border-t text-[11px]" style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>
                Las comisiones se pagan al cierre de cada mes calendario, contra la prima neta efectivamente cobrada por la aseguradora.
              </div>
            </div>

            <div className="rounded-xl p-6 text-white" style={{ background: "var(--brand-navy)" }}>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: "var(--brand-accent)" }}>Tip</div>
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "Manrope" }}>Refiere antes de la renovación</h3>
              <p className="text-sm opacity-85">
                Si la comunidad ya tiene seguro vigente, puedes referirla 60-90 días antes de su renovación para que comparemos opciones y maximicemos tu comisión.
              </p>
            </div>
          </div>
        </div>
      </main>

      {showRefer && <ReferModal onClose={() => setShowRefer(false)} onCreated={load}/>}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, highlight }) {
  return (
    <div className="p-5 bg-white border rounded-xl" style={{ borderColor: "var(--border-subtle)", background: highlight ? "linear-gradient(135deg, #FFFFFF 0%, rgba(217,119,87,0.06) 100%)" : "white" }} data-testid={`stat-${label}`}>
      <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ background: color + "22" }}>
        <Icon size={18} style={{ color }}/>
      </div>
      <div className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope", color: highlight ? "var(--brand-accent)" : "var(--text-primary)" }}>{value}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{label}</div>
    </div>
  );
}

function Row({ label, value, bold, highlight }) {
  return (
    <div className="flex items-center justify-between pb-2 border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span className={`${bold ? "font-bold" : ""}`} style={{ fontFamily: "Manrope", color: highlight ? "var(--brand-accent)" : "var(--text-primary)" }}>{value}</span>
    </div>
  );
}

function ReferModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    razon_social: "", rut_comunidad: "", direccion: "", comuna: "",
    n_unidades: "", contacto_nombre: "", contacto_email: "",
    contacto_telefono: "", contacto_rol: "", notas: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canSubmit = form.razon_social && form.contacto_nombre && form.contacto_email && form.contacto_telefono;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, n_unidades: parseInt(form.n_unidades) || null };
      await http.post("/partners/referidos", payload);
      toast.success("¡Referencia enviada!", { description: "Nuestro equipo contactará a la comunidad pronto." });
      onCreated();
      onClose();
    } catch (e) {
      toast.error("Error al enviar", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,37,64,0.55)" }} onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="refer-modal">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Referir comunidad</h3>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>Nuestro equipo se encarga del resto</p>
          </div>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: "var(--brand-navy)" }}>Datos comunidad</div>
            <div className="grid md:grid-cols-2 gap-4">
              <FldR label="Razón social *"><input className="field-input" value={form.razon_social} onChange={(e) => set("razon_social", e.target.value)} required data-testid="ref-razon"/></FldR>
              <FldR label="RUT comunidad"><input className="field-input" value={form.rut_comunidad} onChange={(e) => set("rut_comunidad", e.target.value)}/></FldR>
              <FldR label="Dirección"><input className="field-input" value={form.direccion} onChange={(e) => set("direccion", e.target.value)}/></FldR>
              <FldR label="Comuna"><input className="field-input" value={form.comuna} onChange={(e) => set("comuna", e.target.value)}/></FldR>
              <FldR label="N° unidades"><input type="number" min="0" className="field-input" value={form.n_unidades} onChange={(e) => set("n_unidades", e.target.value)}/></FldR>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: "var(--brand-navy)" }}>Contacto</div>
            <div className="grid md:grid-cols-2 gap-4">
              <FldR label="Nombre contacto *"><input className="field-input" value={form.contacto_nombre} onChange={(e) => set("contacto_nombre", e.target.value)} required data-testid="ref-contacto-nombre"/></FldR>
              <FldR label="Rol"><input className="field-input" value={form.contacto_rol} onChange={(e) => set("contacto_rol", e.target.value)} placeholder="Presidente comité, administrador..."/></FldR>
              <FldR label="Email contacto *"><input type="email" className="field-input" value={form.contacto_email} onChange={(e) => set("contacto_email", e.target.value)} required data-testid="ref-contacto-email"/></FldR>
              <FldR label="Teléfono contacto *"><input type="tel" className="field-input" value={form.contacto_telefono} onChange={(e) => set("contacto_telefono", e.target.value)} required/></FldR>
            </div>
          </div>
          <FldR label="Notas (opcional)"><textarea rows={3} className="field-input" value={form.notas} onChange={(e) => set("notas", e.target.value)} placeholder="Ej. ya tienen seguro con BCI, vence en marzo..."/></FldR>
        </div>
        <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={!canSubmit || busy} data-testid="ref-submit">
            {busy ? "Enviando..." : "Enviar referencia"}
          </button>
        </div>
      </form>
    </div>
  );
}

const FldR = ({ label, children }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
  </div>
);

function PendingApproval({ partner, onLogout }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bg-soft)" }}>
      <div className="max-w-md text-center bg-white border rounded-xl p-10" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(245,124,0,0.12)" }}>
          <Clock size={28} color="#F57C00" />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "Manrope" }}>Cuenta en revisión</h1>
        <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
          Hola <strong>{partner.nombre}</strong>, tu cuenta partner aún está siendo revisada por nuestro equipo. Te enviaremos un email a <strong>{partner.email}</strong> cuando esté aprobada (típicamente 24h hábiles).
        </p>
        <button onClick={onLogout} className="btn-outline">Cerrar sesión</button>
      </div>
    </div>
  );
}

function BlockedAccount({ partner, onLogout }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bg-soft)" }}>
      <div className="max-w-md text-center bg-white border rounded-xl p-10" style={{ borderColor: "var(--border-subtle)" }}>
        <h1 className="text-2xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "Manrope" }}>Acceso no disponible</h1>
        <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
          Tu cuenta está actualmente <strong>{partner.estado}</strong>. Para más información contáctanos a Lucas@micomunidadsegura.cl.
        </p>
        <button onClick={onLogout} className="btn-outline">Cerrar sesión</button>
      </div>
    </div>
  );
}
